import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { useGate } from 'statsig-react';
import { faClose } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faArrowRight } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { useChannelsEnabled } from '@components/Channels/helpers';
import { IconButton } from '@components/main-components/icon-buttons/IconButton';
import { ChannelsFeatureModal } from '@components/modals/Channels/ChannelsFeature';
import { LeaderboardFeatureModal } from '@components/modals/Leaderboard/LeaderboardFeature';
import { RewardsInfoModal } from '@components/modals/RewardsInfoModal';
import { useAuth } from '@context/auth/base';
import { ModalContext, ModalType } from '@context/modal';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { FEATURE_GATES, SPACE } from '@utils/constants';
import { useHasBanner } from './useHasBanner';

export const NewFeedFeatureBanner = () => {
  const { isAuthenticatedWithAPI } = useAuth();
  const {
    setHasDismissedNewRewardsFeatureBanner,
    setHasDismissedNewLeaderboardFeatureBanner,
    setHasDismissedNewChannelsFeatureBanner,
  } = useBackendUserPersistedData();

  const { value: pointsEnabled } = useGate(FEATURE_GATES.SOCIALS_POINTS);

  const { enabled: channelsEnabled } = useChannelsEnabled();

  const hasBanner = useHasBanner();

  const { openModal } = ModalContext.useContainer();

  if (!hasBanner) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: -50 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -50 }}
      transition={{ duration: 0.3 }}
      className=" relative flex w-full items-center justify-center bg-base900 px-4 py-4 font-base text-base-m text-white lg:px-6"
    >
      <button
        className="before:absolute before:bottom-0 before:left-0 before:right-0 before:top-0 before:content-['']"
        onClick={() => {
          channelsEnabled
            ? openModal(ModalType.CHANNELS_NEW_FEATURE, {
                body: <ChannelsFeatureModal />,
              })
            : pointsEnabled
            ? openModal(ModalType.LEADERBOARD_NEW_FEATURE, {
                body: <LeaderboardFeatureModal />,
              })
            : openModal(ModalType.REWARDS_INFO, {
                body: <RewardsInfoModal />,
              });
        }}
      />

      <div className="inline-flex w-full max-w-content items-center justify-center ">
        {channelsEnabled ? (
          <>
            <span>&#127897;{SPACE}</span>
            <span className="font-semibold">Introducing Channels</span>
          </>
        ) : pointsEnabled ? (
          <>
            <span>&#127897;{SPACE}</span>
            <span className="font-semibold">Introducing Sound Leaderboard</span>
          </>
        ) : (
          <>
            <span>&#x1F4B8;{SPACE}</span>
            <span className="font-semibold">Bigger, Better Rewards</span>
            <span className="hidden lg:block">
              .{SPACE}
              Increased rewards for artists and curators
            </span>
          </>
        )}

        <FontAwesomeIcon
          icon={faArrowRight}
          className="ml-2 font-base text-base100"
          fontSize={16}
        />
      </div>

      {isAuthenticatedWithAPI && (
        <div className="absolute right-1 md:right-4">
          <IconButton
            icon={<FontAwesomeIcon icon={faClose} className="z-above1  font-base text-base100" />}
            iconSize={16}
            variant="inverted"
            onClick={() => {
              channelsEnabled
                ? setHasDismissedNewChannelsFeatureBanner(true)
                : pointsEnabled
                ? setHasDismissedNewLeaderboardFeatureBanner(true)
                : setHasDismissedNewRewardsFeatureBanner(true);
            }}
          />
        </div>
      )}
    </motion.div>
  );
};
